import React, { useRef, useEffect, useLayoutEffect, useState } from "react";
import "./NewsCard.css";
import { formatRelativeTime } from "../../utils/formatRelativeTime";
import { toPersianDigits } from "../../utils/toPersianDigits";
import { watchMediaQuery } from "../../utils/mediaQuery";
import RetryLogoImage from "../media/RetryLogoImage";
import { safeHttpUrl } from "../../utils/safeUrl";

const ACTION_MENU_EVENT = "newsCardActionMenuOpen";
/*
| Mark the card as read as soon as it enters the viewport.
|
| IntersectionObserver's `isIntersecting` flag is the authoritative signal
| here. Keeping the threshold at zero removes the previous wait for half of
| the card to become visible, so the unread badge updates at the moment the
| user reaches the news item while scrolling.
*/
const READ_VISIBILITY_THRESHOLD = 0;

/*
| Keep the source/time metadata on a single line on narrow news cards.
|
| The normal metadata size at the active breakpoint remains the visual
| ceiling. When the row does not have enough horizontal room, only its text is reduced
| until it fits. Fixed card spacing is left untouched. A conservative floor
| protects readability; only exceptionally long labels at exceptionally small
| widths may then fall back to ellipsis instead of wrapping.
*/
const NEWS_META_MIN_FONT_SIZE_PX = 7.5;
const META_FIT_ITERATIONS = 8;
const META_FIT_EPSILON_PX = 0.5;

const getNewsImageUrl = (summary = "") => {
  if (typeof summary !== "string") return null;

  const imgMatch = summary.match(/<img[^>]+src=["']([^"']+)["']/i);
  return imgMatch ? imgMatch[1] : null;
};

const getPlainSummary = (summary = "") => {
  if (typeof summary !== "string") return "";

  return (
    toPersianDigits(
      summary
        .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
        .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/&amp;/gi, "&")
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, " ")
        .trim()
    ) || ""
  );
};

const findScrollContainer = (element) => {
  if (
    !element ||
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return null;
  }

  let current = element.parentElement;

  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);

    if (canScrollY && current.scrollHeight > current.clientHeight) {
      return current;
    }

    current = current.parentElement;
  }

  return document.scrollingElement || document.documentElement;
};

const NewsCard = ({
  news,
  favicon,
  onVisible,
  onViewportExit,
  onOpen,
  isSaved = false,
  onToggleSaved,
  onShare,
  onView,
  mobileDividerEdge = "top",
  eagerImageLoading = false,
}) => {
  const safeNews = news || {};
  const { title = "", summary = "", link = "", source = "", published_str } =
    safeNews;

  const actionMenuKey = String(safeNews?.id ?? link ?? title ?? "");
  const cardId = `news-${encodeURIComponent(
    String(link || safeNews?.id || title || "item")
  )}`;

  const cardRef = useRef(null);
  const metaRef = useRef(null);
  const [, setTick] = useState(0);
  const wasVisibleRef = useRef(false);
  const exitCalledRef = useRef(false);
  const visibleCalledRef = useRef(false);

  const [isMobile, setIsMobile] = useState(false);
  const [shareState, setShareState] = useState("idle");
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);

  const imageUrl = getNewsImageUrl(summary);
  const plainSummary = getPlainSummary(summary);
  const relativeTime = published_str ? formatRelativeTime(published_str) : "";

  const [failedImageUrl, setFailedImageUrl] = useState(null);
  const [loadedImageUrl, setLoadedImageUrl] = useState(null);

  const shouldShowImage = Boolean(imageUrl) && failedImageUrl !== imageUrl;
  const isImageLoaded = shouldShowImage && loadedImageUrl === imageUrl;

  useEffect(() => {
    return watchMediaQuery("(max-width: 767px)", setIsMobile);
  }, []);

  useLayoutEffect(() => {
    const meta = metaRef.current;

    if (!meta || typeof window === "undefined") {
      return undefined;
    }

    let frameId = null;
    let resizeObserver = null;
    let cancelled = false;
    let lastObservedWidth = meta.clientWidth;

    const clearResponsiveFit = () => {
      meta.style.removeProperty("--news-card-meta-fit-font-size");
      meta.classList.remove("news-card__meta--min-font-fallback");
    };

    /*
    | Measure only the real flex items, not scrollWidth. On mobile the
    | action-menu button has an oversized invisible ::before hit target
    | (inset: -12px). That pseudo-element contributes to scroll overflow
    | even when every visible metadata item fits, which previously made
    | normal mobile rows shrink all the way toward the minimum font size.
    | getBoundingClientRect() on the actual children excludes that pseudo
    | hit area and therefore reflects the visible row we actually need to fit.
    */
    const rowFits = () => {
      const metaRect = meta.getBoundingClientRect();

      /*
      | Measure only children that actually participate in the rendered row.
      | The mobile action-menu button still exists in the DOM on desktop, but
      | CSS hides it with display:none. A hidden element has a zero rect at the
      | document origin, so treating it as a visible flex item makes every
      | desktop row look out of bounds and incorrectly forces the metadata down
      | toward the minimum font size. getClientRects() cleanly excludes those
      | non-rendered children while keeping the visible source/time items.
      */
      const visibleChildren = Array.from(meta.children).filter(
        (child) => child.getClientRects().length > 0
      );

      return visibleChildren.every((child) => {
        const childRect = child.getBoundingClientRect();

        return (
          childRect.left >= metaRect.left - META_FIT_EPSILON_PX &&
          childRect.right <= metaRect.right + META_FIT_EPSILON_PX
        );
      });
    };

    const fitMetadataRow = () => {
      if (cancelled || !meta.isConnected) return;

      clearResponsiveFit();

      if (rowFits()) {
        return;
      }

      const sourceElement = meta.querySelector(".news-card__source");
      const computedFontSize = Number.parseFloat(
        window.getComputedStyle(sourceElement || meta).fontSize
      );

      if (!Number.isFinite(computedFontSize)) {
        return;
      }

      const maximumFontSize = computedFontSize;
      const minimumFontSize = Math.min(
        NEWS_META_MIN_FONT_SIZE_PX,
        maximumFontSize
      );

      let low = minimumFontSize;
      let high = maximumFontSize;

      meta.style.setProperty(
        "--news-card-meta-fit-font-size",
        `${minimumFontSize}px`
      );

      if (!rowFits()) {
        meta.classList.add("news-card__meta--min-font-fallback");
        return;
      }

      for (let index = 0; index < META_FIT_ITERATIONS; index += 1) {
        const candidate = (low + high) / 2;

        meta.style.setProperty(
          "--news-card-meta-fit-font-size",
          `${candidate}px`
        );

        if (rowFits()) {
          low = candidate;
        } else {
          high = candidate;
        }
      }

      meta.style.setProperty(
        "--news-card-meta-fit-font-size",
        `${low}px`
      );
    };

    const scheduleFit = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        fitMetadataRow();
      });
    };

    /* useLayoutEffect lets the first fit complete before the browser paints. */
    fitMetadataRow();

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(([entry]) => {
        const nextWidth = entry?.contentRect?.width ?? meta.clientWidth;

        /*
        | Font fitting can change the row height. Ignore height-only observer
        | notifications so the fit does not re-trigger itself frame after
        | frame. Recalculate only when the available horizontal space changes.
        */
        if (Math.abs(nextWidth - lastObservedWidth) <= META_FIT_EPSILON_PX) {
          return;
        }

        lastObservedWidth = nextWidth;
        scheduleFit();
      });
      resizeObserver.observe(meta);
    } else {
      window.addEventListener("resize", scheduleFit);
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) scheduleFit();
      });
    }

    return () => {
      cancelled = true;

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }

      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleFit);
      clearResponsiveFit();
    };
  }, [isMobile, source, relativeTime, favicon]);

  useEffect(() => {
    wasVisibleRef.current = false;
    exitCalledRef.current = false;
    visibleCalledRef.current = false;
  }, [safeNews?.id, link]);

  useEffect(() => {
    const element = cardRef.current;
    if (!element) return undefined;

    /*
    | Do not start read-visibility tracking while the parent feed is still
    | preparing behind its skeleton. During that phase VirtualizedNewsList
    | intentionally has no real onMarkAsRead handler. Marking this card as
    | "already reported" there would make the initially visible first card
    | miss its real read event after the feed is revealed.
    |
    | Once onVisible becomes available, this effect runs again and
    | IntersectionObserver immediately evaluates the current card position.
    */
    if (!onVisible && !onViewportExit) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      if (onVisible && !visibleCalledRef.current) {
        visibleCalledRef.current = true;
        onVisible();
      }

      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isReadVisibilityReached = entry.isIntersecting;

        if (isReadVisibilityReached) {
          if (onVisible && !visibleCalledRef.current) {
            visibleCalledRef.current = true;
            onVisible();
          }

          wasVisibleRef.current = true;
        }

        if (
          onViewportExit &&
          !entry.isIntersecting &&
          wasVisibleRef.current &&
          !exitCalledRef.current
        ) {
          exitCalledRef.current = true;
          onViewportExit();
          observer.unobserve(element);
        }
      },
      { threshold: [0, READ_VISIBILITY_THRESHOLD] }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [onVisible, onViewportExit, safeNews?.id, link]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTick((tick) => tick + 1);
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const closeForAnotherCard = (event) => {
      if (event.detail?.key !== actionMenuKey) {
        setIsActionMenuOpen(false);
      }
    };

    window.addEventListener(ACTION_MENU_EVENT, closeForAnotherCard);

    return () => {
      window.removeEventListener(ACTION_MENU_EVENT, closeForAnotherCard);
    };
  }, [actionMenuKey]);

  useEffect(() => {
    if (!isActionMenuOpen || !isMobile || typeof window === "undefined") {
      return undefined;
    }

    const closeOnScroll = () => setIsActionMenuOpen(false);

    window.addEventListener("scroll", closeOnScroll, true);

    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
    };
  }, [isActionMenuOpen, isMobile]);

  useEffect(() => {
    if (!isMobile || !isActionMenuOpen || !cardRef.current) return undefined;

    const card = cardRef.current;
    const listRoot =
      card.closest(
        ".most-visited-pager__page, .focus-pager__page, .news-grid, .virtualized-news-list"
      ) || card.parentElement;

    /*
    | Blur the divider that visually touches the open card.
    |
    | Mobile Focus moves dividers to the bottom edge. When a Regular/First Ad
    | sits directly above the news card, that shared top boundary belongs to
    | the ad slot, not to the previous news card. Walk the real visual item
    | order (news + ads) so we never blur a divider two items away.
    */
    const visualItems = Array.from(
      listRoot?.querySelectorAll?.(
        ".news-card:not(.news-card--load-skeleton), " +
          ".first-ad-card-slot, .regular-ad-card-slot"
      ) || []
    );

    const cardIndex = visualItems.indexOf(card);
    const neighbor =
      cardIndex < 0
        ? null
        : mobileDividerEdge === "top"
          ? visualItems[cardIndex + 1] || null
          : mobileDividerEdge === "bottom" && cardIndex > 0
            ? visualItems[cardIndex - 1]
            : null;

    const neighborIsAd = Boolean(
      neighbor?.matches?.(".first-ad-card-slot, .regular-ad-card-slot")
    );
    const neighborClass = neighborIsAd
      ? "ad-card-slot--neighbor-actions-open"
      : "news-card--neighbor-actions-open";

    neighbor?.classList.add(neighborClass);

    return () => {
      neighbor?.classList.remove(neighborClass);
    };
  }, [isActionMenuOpen, isMobile, mobileDividerEdge]);

  useEffect(() => {
    setLoadedImageUrl(null);
    setFailedImageUrl(null);
  }, [imageUrl]);

  const handleImageFailure = () => {
    if (imageUrl) {
      setFailedImageUrl(imageUrl);
    }
  };

  const handleImageLoad = (event) => {
    const image = event.currentTarget;

    if (!image.complete || image.naturalWidth === 0) {
      handleImageFailure();
      return;
    }

    setLoadedImageUrl(imageUrl);
  };

  const openNews = () => {
    const safeLink = safeHttpUrl(link);
    if (!safeLink || typeof document === "undefined") return;

    onOpen?.(safeNews);

    const anchor = document.createElement("a");
    anchor.href = safeLink;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    anchor.click();
  };

  const handleCardClick = () => {
    openNews();
  };

  const handleCardKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    openNews();
  };

  const handleImageClick = (event) => {
    event.stopPropagation();
    openNews();
  };

  const handleActionMenuClick = (event) => {
    event.stopPropagation();
    event.currentTarget.blur();

    setIsActionMenuOpen((open) => {
      const nextOpen = !open;

      if (nextOpen && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(ACTION_MENU_EVENT, {
            detail: { key: actionMenuKey },
          })
        );
      }

      return nextOpen;
    });
  };

  const handleSaveClick = (event) => {
    event.stopPropagation();
    event.currentTarget.blur();
    setIsActionMenuOpen(false);
    onToggleSaved?.(safeNews);
  };

  const handleShareClick = async (event) => {
    event.stopPropagation();
    event.currentTarget.blur();

    if (!onShare) return;

    try {
      await onShare(safeNews);
      setShareState("copied");
    } catch {
      setShareState("failed");
    }

    setTimeout(() => {
      setShareState("idle");
      setIsActionMenuOpen(false);
    }, 1000);
  };

  const handleViewClick = (event) => {
    event.stopPropagation();
    event.currentTarget.blur();
    setIsActionMenuOpen(false);

    const scrollContainer = findScrollContainer(cardRef.current);

    onView?.(safeNews, {
      scrollContainer,
      scrollTop: scrollContainer?.scrollTop ?? null,
      windowX: typeof window !== "undefined" ? window.scrollX : 0,
      windowY: typeof window !== "undefined" ? window.scrollY : 0,
    });
  };

  const renderActionMenuButton = () => (
    <button
      type="button"
      className={`news-card__action-menu-button${
        isActionMenuOpen ? " news-card__action-menu-button--active" : ""
      }`}
      onClick={handleActionMenuClick}
      aria-label={
        isActionMenuOpen ? "بستن گزینه‌های خبر" : "نمایش گزینه‌های خبر"
      }
      aria-expanded={isActionMenuOpen}
      title={isActionMenuOpen ? "بستن گزینه‌های خبر" : "نمایش گزینه‌های خبر"}
    >
      <svg
        className="news-card__action-menu-icon"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle cx="6" cy="12" r="1.6" />
        <circle cx="12" cy="12" r="1.6" />
        <circle cx="18" cy="12" r="1.6" />
      </svg>
    </button>
  );

  const renderActions = (modifierClass) => (
    <div
      className={`news-card__actions ${modifierClass}${
        isActionMenuOpen ? " news-card__actions--open" : ""
      }`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className={`news-card__save-button${
          isSaved ? " news-card__save-button--active" : ""
        }`}
        onClick={handleSaveClick}
        aria-label={isSaved ? "حذف از ذخیره‌شده‌ها" : "ذخیره خبر"}
        title={isSaved ? "حذف از ذخیره‌شده‌ها" : "ذخیره خبر"}
      >
        <svg
          className="news-card__save-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M7 4.5C7 3.7 7.7 3 8.5 3h7c.8 0 1.5.7 1.5 1.5V20l-5-3.5L7 20V4.5Z" />
        </svg>
      </button>

      <button
        type="button"
        className="news-card__view-button"
        onClick={handleViewClick}
        aria-label="نمایش کارت خبر"
        title="نمایش کارت خبر"
      >
        <svg
          className="news-card__view-icon"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>

      <button
        type="button"
        className={`news-card__share-button${
          shareState === "copied" ? " news-card__share-button--done" : ""
        }${shareState === "failed" ? " news-card__share-button--failed" : ""}`}
        data-news-id={safeNews?.id ?? ""}
        onClick={handleShareClick}
        aria-label={
          shareState === "copied" ? "لینک خبر کپی شد" : "کپی لینک خبر"
        }
        title={shareState === "copied" ? "لینک کپی شد" : "کپی لینک"}
      >
        {shareState === "copied" ? (
          <svg
            className="news-card__share-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M5 12.5l4.2 4.2L19 7" />
          </svg>
        ) : (
          <svg
            className="news-card__share-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M9.5 14.5l5-5" />
            <path d="M10.8 7.2l1.1-1.1a4 4 0 0 1 5.7 5.7l-1.1 1.1" />
            <path d="M13.2 16.8l-1.1 1.1a4 4 0 0 1-5.7-5.7l1.1-1.1" />
          </svg>
        )}
      </button>
    </div>
  );

  return (
    <article
      id={cardId}
      data-news-id={safeNews?.id ?? ""}
      ref={cardRef}
      className={`news-card ${
        !isImageLoaded ? "news-card--has-placeholder" : ""
      }${isActionMenuOpen ? " news-card--actions-open" : ""}`}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      tabIndex={isMobile ? -1 : 0}
      role="link"
      aria-label={toPersianDigits(title)}
    >
      <div className="news-card__mobile-blur-overlay" aria-hidden="true" />

      <div className="news-card__content">
        <h2 className="news-card__title">{toPersianDigits(title)}</h2>

        {plainSummary && <p className="news-card__summary">{plainSummary}</p>}

        <div ref={metaRef} className="news-card__meta">
          {renderActionMenuButton()}

          {favicon && (
            <span className="news-card__source-icon-wrapper">
              <RetryLogoImage
                src={favicon}
                alt=""
                className="news-card__source-icon"
                fallbackText={source?.charAt(0) || "N"}
                width="16"
                height="16"
                loading="lazy"
                decoding="async"
              />
            </span>
          )}

          <span className="news-card__source">{toPersianDigits(source)}</span>

          {relativeTime && (
            <>
              <span className="news-card__dot">•</span>
              <span className="news-card__time">{relativeTime}</span>
            </>
          )}
        </div>
      </div>

      <div
        className={`news-card__image-wrapper ${
          !isImageLoaded ? "news-card__image-wrapper--placeholder" : ""
        }`}
        onClick={handleImageClick}
      >
        {shouldShowImage && (
          <img
            className={`news-card__image${
              !isImageLoaded ? " news-card__image--loading" : ""
            }`}
            src={imageUrl}
            alt=""
            loading={eagerImageLoading ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eagerImageLoading ? "high" : "auto"}
            onError={handleImageFailure}
            onLoad={handleImageLoad}
          />
        )}

        {!isImageLoaded && (
          <div className="news-card__placeholder">
            {favicon ? (
              <RetryLogoImage
                src={favicon}
                alt=""
                className="news-card__placeholder-logo"
                fallbackText={source?.charAt(0) || "N"}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <span className="news-card__placeholder-letter">
                {source?.charAt(0) || "N"}
              </span>
            )}
          </div>
        )}

        {renderActionMenuButton()}
        {renderActions("news-card__actions--image")}
      </div>

      {renderActions("news-card__actions--card")}
    </article>
  );
};

export default NewsCard;
